import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Check, Loader2, Plus, Trash2 } from 'lucide-react';
import {
  api,
  type AuthUser,
  type PhraseCategory,
  type PhraseEntry,
  type PhrasePool,
} from '@/lib/api';
import { cn } from '@/lib/utils';

const CATEGORY_LABEL: Record<PhraseCategory, string> = {
  chat_ban: 'Chat bans',
  strength: 'Strength push winners',
  vs_slacker: 'VS slackers · under target yesterday',
  chase: 'Self-Report · The chase',
  worst_parker: 'Worst parker of the week',
};

export default function AdminPhrases({ user }: { user: AuthUser }) {
  const canEdit = user.role === 'R5' || user.role === 'R4';
  const q = useQuery({
    queryKey: ['admin-phrases'],
    queryFn: api.listAdminPhrases,
    enabled: canEdit,
  });
  const [selected, setSelected] = useState<string | null>(null);

  if (!canEdit) {
    return (
      <div className="mx-auto w-full max-w-3xl space-y-3 p-4">
        <h1 className="text-xl font-semibold">Phrases</h1>
        <p className="rounded border border-slate-800 bg-slate-900/40 p-3 text-sm text-slate-400">
          R4/R5 required to manage phrases.
        </p>
      </div>
    );
  }

  const pools = q.data?.pools ?? [];
  const phrases = q.data?.phrases ?? [];
  const activePool = selected ?? pools[0]?.poolKey ?? null;

  const grouped = useMemo(() => {
    const map = new Map<PhraseCategory, PhrasePool[]>();
    for (const p of pools) {
      const list = map.get(p.category) ?? [];
      list.push(p);
      map.set(p.category, list);
    }
    return map;
  }, [pools]);

  const counts = useMemo(() => {
    const m = new Map<string, { active: number; total: number }>();
    for (const p of phrases) {
      const cur = m.get(p.poolKey) ?? { active: 0, total: 0 };
      cur.total += 1;
      if (p.active) cur.active += 1;
      m.set(p.poolKey, cur);
    }
    return m;
  }, [phrases]);

  const currentPool = pools.find((p) => p.poolKey === activePool) ?? null;
  const currentPhrases = phrases.filter((p) => p.poolKey === activePool);

  return (
    <div className="mx-auto w-full max-w-5xl space-y-4 p-4">
      <header>
        <h1 className="text-xl font-semibold">Phrases</h1>
        <p className="text-sm text-slate-400">
          Editable joke lines used in the alliance hub and self-report pages.
          Deactivate a line to hide it without deleting it — hash-based
          selection stays deterministic per pool size.
        </p>
      </header>

      {q.isLoading ? (
        <div className="text-sm text-slate-500">Loading…</div>
      ) : (
        <div className="grid gap-4 md:grid-cols-[minmax(0,260px)_minmax(0,1fr)]">
          <aside className="space-y-4">
            {[...grouped.entries()].map(([category, catPools]) => (
              <div key={category} className="space-y-1">
                <div className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">
                  {CATEGORY_LABEL[category] ?? category}
                </div>
                <div className="space-y-0.5">
                  {catPools.map((p) => {
                    const c = counts.get(p.poolKey) ?? { active: 0, total: 0 };
                    return (
                      <button
                        key={p.poolKey}
                        type="button"
                        onClick={() => setSelected(p.poolKey)}
                        className={cn(
                          'flex w-full items-center justify-between gap-2 rounded border px-2 py-1.5 text-left text-xs',
                          p.poolKey === activePool
                            ? 'border-indigo-500 bg-indigo-950/40 text-slate-100'
                            : 'border-slate-800 bg-slate-900/40 text-slate-300 hover:border-slate-700',
                        )}
                      >
                        <span className="truncate">{p.label}</span>
                        <span
                          className={cn(
                            'shrink-0 rounded px-1 text-[10px] tabular-nums',
                            c.active === 0
                              ? 'bg-rose-900/40 text-rose-200'
                              : 'bg-slate-800 text-slate-300',
                          )}
                        >
                          {c.active}/{c.total}
                        </span>
                      </button>
                    );
                  })}
                </div>
              </div>
            ))}
          </aside>

          <section className="space-y-3">
            {currentPool ? (
              <PoolEditor pool={currentPool} phrases={currentPhrases} />
            ) : (
              <div className="text-sm text-slate-500">
                No pool selected.
              </div>
            )}
          </section>
        </div>
      )}
    </div>
  );
}

function PoolEditor({
  pool,
  phrases,
}: {
  pool: PhrasePool;
  phrases: PhraseEntry[];
}) {
  const qc = useQueryClient();
  const invalidate = () =>
    qc.invalidateQueries({ queryKey: ['admin-phrases'] });

  const createMut = useMutation({
    mutationFn: (text: string) => api.createAdminPhrase(pool.poolKey, text),
    onSuccess: invalidate,
  });

  const [newText, setNewText] = useState('');

  return (
    <div className="space-y-3">
      <div className="rounded border border-slate-800 bg-slate-900/40 p-3">
        <div className="text-sm font-semibold text-slate-100">
          {pool.label}
        </div>
        {pool.description && (
          <div className="mt-0.5 text-xs text-slate-400">{pool.description}</div>
        )}
      </div>

      <form
        className="flex items-center gap-2"
        onSubmit={(e) => {
          e.preventDefault();
          const t = newText.trim();
          if (t.length === 0) return;
          createMut.mutate(t, {
            onSuccess: () => setNewText(''),
          });
        }}
      >
        <input
          value={newText}
          onChange={(e) => setNewText(e.target.value)}
          placeholder="Add a new line…"
          maxLength={240}
          className="flex-1 rounded border border-slate-700 bg-slate-950 px-2 py-1.5 text-sm text-slate-100 placeholder:text-slate-500 focus:border-indigo-500 focus:outline-none"
        />
        <button
          type="submit"
          disabled={createMut.isPending || newText.trim().length === 0}
          className="inline-flex items-center gap-1 rounded bg-indigo-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-indigo-500 disabled:opacity-50"
        >
          {createMut.isPending ? (
            <Loader2 size={14} className="animate-spin" />
          ) : (
            <Plus size={14} />
          )}
          Add
        </button>
      </form>

      <ul className="space-y-1">
        {phrases.length === 0 && (
          <li className="rounded border border-dashed border-slate-800 p-3 text-xs text-slate-500">
            No lines yet — add one above.
          </li>
        )}
        {phrases.map((p) => (
          <PhraseRow key={p.id} phrase={p} />
        ))}
      </ul>
    </div>
  );
}

function PhraseRow({ phrase }: { phrase: PhraseEntry }) {
  const qc = useQueryClient();
  const invalidate = () =>
    qc.invalidateQueries({ queryKey: ['admin-phrases'] });

  const [text, setText] = useState(phrase.text);
  const dirty = text.trim() !== phrase.text && text.trim().length > 0;

  const patchMut = useMutation({
    mutationFn: (body: { text?: string; active?: boolean }) =>
      api.updateAdminPhrase(phrase.id, body),
    onSuccess: invalidate,
  });
  const deleteMut = useMutation({
    mutationFn: () => api.deleteAdminPhrase(phrase.id),
    onSuccess: invalidate,
  });

  return (
    <li
      className={cn(
        'flex items-center gap-2 rounded border px-2 py-1.5',
        phrase.active
          ? 'border-slate-800 bg-slate-900/40'
          : 'border-slate-800 bg-slate-900/20 opacity-60',
      )}
    >
      <input
        type="checkbox"
        checked={phrase.active}
        onChange={(e) => patchMut.mutate({ active: e.target.checked })}
        title={phrase.active ? 'Deactivate' : 'Activate'}
        className="h-4 w-4"
      />
      <input
        value={text}
        onChange={(e) => setText(e.target.value)}
        onBlur={() => {
          if (dirty) patchMut.mutate({ text: text.trim() });
        }}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            e.currentTarget.blur();
          } else if (e.key === 'Escape') {
            setText(phrase.text);
            e.currentTarget.blur();
          }
        }}
        maxLength={240}
        className="flex-1 rounded bg-transparent px-1 py-0.5 text-sm text-slate-100 focus:bg-slate-950 focus:outline-none"
      />
      {dirty && (
        <button
          type="button"
          onClick={() => patchMut.mutate({ text: text.trim() })}
          className="rounded p-1 text-emerald-400 hover:bg-emerald-900/40"
          title="Save"
        >
          <Check size={14} />
        </button>
      )}
      <button
        type="button"
        onClick={() => {
          if (confirm('Delete this line?')) deleteMut.mutate();
        }}
        className="rounded p-1 text-rose-400 hover:bg-rose-900/40"
        title="Delete"
      >
        <Trash2 size={14} />
      </button>
    </li>
  );
}
